import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { CrmAccount, CrmClient, CrmClientCommunication, CrmClientContactDirectoryEntry } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { CustomerService } from '../../customer/customer.service';
import { Party } from '../../party/party.model';
import { PartyService } from '../../party/party.service';
import { PartyType } from '../../party/party-type.enum';
import { CRM_RECEIVABLE_ROLES, CrmPartyContextService } from '../crm-client-contact-picker/crm-party-context.service';
import { CommercialPartyReference, CommercialPartySource, normalizeCommercialPartyReference } from './commercial-party.model';
import { CrmQuickCreateDialogComponent, CrmQuickCreateMode, CrmQuickCreateResult } from '../crm-quick-create/crm-quick-create-dialog.component';

@Component({
    selector: 'app-commercial-party-picker',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, IftaLabelModule, InputGroupModule, InputGroupAddonModule, SelectModule, TagModule, CrmQuickCreateDialogComponent],
    template: `
        <div class="commercial-party-picker">
            <div class="party-picker-header">
                <div>
                    <span class="party-picker-title">{{ label }}</span>
                    <small>Choose a CRM client or a Party, including a company, driver, or other person.</small>
                </div>
                <div class="party-source-toggle" *ngIf="allowLegacy">
                    <p-button label="CRM" icon="pi pi-star-fill" size="small"
                        [outlined]="source !== 'CRM'" [disabled]="disabled"
                        (onClick)="selectSource('CRM')"></p-button>
                    <p-button label="Party" icon="pi pi-users" size="small" severity="secondary"
                        [outlined]="source === 'CRM'" [disabled]="disabled"
                        (onClick)="selectSource(partySource)"></p-button>
                </div>
                <p-tag *ngIf="!allowLegacy" value="CRM" icon="pi pi-star-fill" severity="success"></p-tag>
            </div>

            <div class="party-fields" *ngIf="source === 'CRM'">
                <div class="party-field-with-create">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
                    <p-iftalabel>
                        <p-select inputId="commercialCrmClient" [options]="clients" [(ngModel)]="selectedClient"
                            optionLabel="companyName" [filter]="true"
                            filterBy="companyName,customerNameLocal,clientNumber,primaryEmail,phoneNumber"
                            [loading]="loading" [disabled]="disabled" [showClear]="!required"
                            appendTo="body" styleClass="w-full" (onChange)="onClientChange()"></p-select>
                        <label for="commercialCrmClient">CRM Client{{ required ? ' *' : '' }}</label>
                    </p-iftalabel>
                </p-inputGroup>
                <p-button *ngIf="!disabled" icon="pi pi-plus" size="small" severity="secondary" [outlined]="true"
                    ariaLabel="Create CRM Client" (onClick)="openQuickCreate('client')"></p-button>
                </div>
                <div class="party-field-with-create">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-briefcase"></i></p-inputGroupAddon>
                    <p-iftalabel>
                        <p-select inputId="commercialCrmAccount" [options]="accounts" [(ngModel)]="selectedAccount"
                            optionLabel="companyName" [filter]="true" filterBy="companyName,category"
                            [disabled]="disabled || !selectedClient" [showClear]="!requireCrmAccount"
                            appendTo="body" styleClass="w-full" (onChange)="onAccountChange()"></p-select>
                        <label for="commercialCrmAccount">CRM Account{{ requireCrmAccount ? ' *' : '' }}</label>
                    </p-iftalabel>
                </p-inputGroup>
                <p-button *ngIf="!disabled" icon="pi pi-plus" size="small" severity="secondary" [outlined]="true"
                    ariaLabel="Create CRM Client Account" [disabled]="!selectedClient" (onClick)="openQuickCreate('account')"></p-button>
                </div>
                <div class="party-field-with-create">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                    <p-iftalabel>
                        <p-select inputId="commercialCrmContact" [options]="contacts" [(ngModel)]="selectedContact"
                            optionLabel="name" [filter]="true" filterBy="name,email,phone,mobile"
                            [disabled]="disabled || !selectedClient" [showClear]="true"
                            appendTo="body" styleClass="w-full" (onChange)="onContactChange()">
                            <ng-template pTemplate="selectedItem" let-contact>
                                <span>{{ contactDisplayName(contact) }}</span>
                            </ng-template>
                            <ng-template pTemplate="item" let-contact>
                                <div class="directory-contact-option">
                                    <strong>{{ contactDisplayName(contact) }}</strong>
                                    <span>{{ contact.email || contact.phone || contact.mobile || 'Contact details pending' }}</span>
                                    <div class="directory-contact-badges">
                                        <p-tag *ngIf="contact.clientContact" value="Client contact" severity="info"></p-tag>
                                        <p-tag *ngIf="contact.linkedCompanyContact" value="Linked company" severity="success"></p-tag>
                                        <p-tag *ngFor="let account of contact.accounts || []" [value]="'Account: ' + (account.name || account.id)" severity="secondary"></p-tag>
                                    </div>
                                </div>
                            </ng-template>
                        </p-select>
                        <label for="commercialCrmContact">CRM Contact</label>
                    </p-iftalabel>
                </p-inputGroup>
                <p-button *ngIf="!disabled" icon="pi pi-plus" size="small" severity="secondary" [outlined]="true"
                    ariaLabel="Create CRM Client Contact" [disabled]="!selectedClient" (onClick)="openQuickCreate('contact')"></p-button>
                </div>
            </div>

            <div class="party-fields legacy" *ngIf="source !== 'CRM'">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon>
                    <p-iftalabel>
                        <p-select inputId="commercialLegacyParty" [options]="legacyParties" [(ngModel)]="selectedLegacyParty"
                            optionLabel="entityName" [filter]="true"
                            filterBy="entityName,id.entityType,crNumber,taxRegNumber,emailAddress,phoneNumber,address"
                            [loading]="loading" [disabled]="disabled" [showClear]="!required"
                            appendTo="body" styleClass="w-full" (onChange)="onLegacyPartyChange()"
                            (onFilter)="onPartyFilter($event.filter)">
                            <ng-template pTemplate="selectedItem" let-party>
                                <span class="legacy-party-selected">
                                    <span class="legacy-party-selected-name">{{ party.entityName }}</span>
                                    <span class="legacy-party-type" [class.company]="party.id.entityType === partyType.Company">{{ party.id.entityType }}</span>
                                </span>
                            </ng-template>
                            <ng-template pTemplate="item" let-party>
                                <div class="legacy-party-option">
                                    <div class="legacy-party-option-heading">
                                        <strong>{{ party.entityName }}</strong>
                                        <span class="legacy-party-type" [class.company]="party.id.entityType === partyType.Company">{{ party.id.entityType }}</span>
                                    </div>
                                    <div class="legacy-party-option-details">
                                        <span>ID {{ party.id.entityId }}</span>
                                        <span *ngIf="party.crNumber">CR {{ party.crNumber }}</span>
                                        <span *ngIf="party.taxRegNumber">VAT {{ party.taxRegNumber }}</span>
                                        <span *ngIf="party.emailAddress">{{ party.emailAddress }}</span>
                                        <span *ngIf="party.phoneNumber">{{ party.phoneNumber }}</span>
                                        <span *ngIf="party.address">{{ party.address }}</span>
                                    </div>
                                </div>
                            </ng-template>
                        </p-select>
                        <label for="commercialLegacyParty">Party{{ required ? ' *' : '' }}</label>
                    </p-iftalabel>
                </p-inputGroup>
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
                    <p-iftalabel>
                        <p-select inputId="commercialLegacyContact" [options]="legacyContacts" [(ngModel)]="selectedLegacyContact"
                            optionLabel="entityName" [filter]="true" filterBy="entityName,emailAddress,phoneNumber"
                            [disabled]="disabled || !selectedLegacyParty || selectedLegacyParty.id.entityType !== partyType.Company"
                            [showClear]="true" appendTo="body" styleClass="w-full" (onChange)="emitSelection()">
                            <ng-template pTemplate="item" let-contact>
                                <div class="legacy-party-option">
                                    <div class="legacy-party-option-heading">
                                        <strong>{{ contact.entityName }}</strong>
                                        <span class="legacy-party-type">{{ contact.id.entityType }}</span>
                                    </div>
                                    <div class="legacy-party-option-details">
                                        <span>ID {{ contact.id.entityId }}</span>
                                        <span *ngIf="contact.emailAddress">{{ contact.emailAddress }}</span>
                                        <span *ngIf="contact.phoneNumber">{{ contact.phoneNumber }}</span>
                                    </div>
                                </div>
                            </ng-template>
                        </p-select>
                        <label for="commercialLegacyContact">Party Contact</label>
                    </p-iftalabel>
                </p-inputGroup>
            </div>

            <small class="party-required" *ngIf="required && !hasRequiredSelection()">
                {{ requireCrmAccount ? 'Select a CRM client and CRM account.' : 'Select a commercial party.' }}
            </small>
        </div>
        <app-crm-quick-create-dialog *ngIf="quickCreateMode" [visible]="quickCreateVisible"
            (visibleChange)="quickCreateVisible = $event" [mode]="quickCreateMode"
            [owningCompanyId]="owningCompanyId" [client]="selectedClient" [account]="selectedAccount"
            [allowedRoles]="roles" (created)="onQuickCreated($event)"></app-crm-quick-create-dialog>
    `,
    styles: [`
        :host { display: block; width: 100%; }
        .commercial-party-picker { border: 1px solid var(--surface-border); border-radius: 12px; padding: 1rem; background: var(--surface-card); }
        .party-picker-header { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: .9rem; }
        .party-picker-header > div:first-child { display: flex; flex-direction: column; gap: .2rem; }
        .party-picker-title { font-weight: 700; color: var(--text-color); }
        .party-picker-header small { color: var(--text-color-secondary); }
        .party-source-toggle { display: flex; gap: .4rem; flex: 0 0 auto; }
        .party-fields { display: grid; grid-template-columns: minmax(15rem, 2fr) minmax(12rem, 1fr) minmax(12rem, 1fr); gap: .75rem; }
        .party-fields.legacy { grid-template-columns: minmax(15rem, 2fr) minmax(12rem, 1fr); }
        .party-fields p-inputgroup, .party-fields p-iftalabel, .party-fields p-select { width: 100%; }
        .party-field-with-create { display: flex; align-items: center; gap: .35rem; min-width: 0; }
        .party-field-with-create p-inputgroup { flex: 1; min-width: 0; }
        .party-required { color: var(--p-red-500); display: block; margin-top: .5rem; }
        .directory-contact-option { display: grid; gap: .2rem; min-width: 0; }
        .directory-contact-option strong, .directory-contact-option > span { overflow-wrap: anywhere; }
        .directory-contact-option > span { color: var(--text-color-secondary); font-size: .82rem; }
        .directory-contact-badges { display: flex; flex-wrap: wrap; gap: .25rem; margin-top: .15rem; }
        .directory-contact-badges ::ng-deep .p-tag { font-size: .68rem; }
        .legacy-party-selected { display: inline-flex; align-items: center; gap: .45rem; max-width: 100%; min-width: 0; }
        .legacy-party-selected-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .legacy-party-option { display: grid; gap: .3rem; min-width: 0; width: 100%; }
        .legacy-party-option-heading { display: flex; align-items: center; flex-wrap: wrap; gap: .4rem; }
        .legacy-party-option-heading strong { overflow-wrap: anywhere; }
        .legacy-party-type { flex: 0 0 auto; border-radius: 999px; padding: .12rem .45rem; font-size: .72rem; font-weight: 700; color: var(--p-teal-700); background: var(--p-teal-50); }
        .legacy-party-type.company { color: var(--p-blue-700); background: var(--p-blue-50); }
        .legacy-party-option-details { display: flex; flex-wrap: wrap; gap: .15rem .7rem; color: var(--text-color-secondary); font-size: .78rem; overflow-wrap: anywhere; }
        @media (max-width: 900px) { .party-fields, .party-fields.legacy { grid-template-columns: 1fr; } .party-picker-header { align-items: flex-start; } }
    `]
})
export class CommercialPartyPickerComponent implements OnInit, OnChanges, OnDestroy {
    @Input() owningCompanyId?: number;
    @Input() value?: Partial<CommercialPartyReference> | null;
    @Input() partySnapshot?: Party | null;
    @Input() partyContactSnapshot?: Party | null;
    @Input() crmClientSnapshot?: CrmClient | null;
    @Input() crmContactSnapshot?: CrmClientCommunication | null;
    @Input() roles: string[] = CRM_RECEIVABLE_ROLES;
    @Input() label = 'Commercial Party';
    @Input() required = false;
    @Input() requireCrmAccount = false;
    @Input() allowLegacy = true;
    @Input() partySource: 'LEGACY' | 'PARTY' = 'LEGACY';
    @Input() preserveLegacySelection = false;
    @Input() preservePartySelection = false;
    @Input() disabled = false;
    @Output() valueChange = new EventEmitter<CommercialPartyReference>();
    @Output() quickCreated = new EventEmitter<CrmQuickCreateResult>();

    readonly partyType = PartyType;
    source: CommercialPartySource = 'CRM';
    clients: CrmClient[] = [];
    accounts: CrmAccount[] = [];
    contacts: CrmClientContactDirectoryEntry[] = [];
    legacyParties: Party[] = [];
    legacyContacts: Party[] = [];
    selectedClient: CrmClient | null = null;
    selectedAccount: CrmAccount | null = null;
    selectedContact: CrmClientContactDirectoryEntry | null = null;
    selectedLegacyParty: Party | null = null;
    selectedLegacyContact: Party | null = null;
    loading = false;
    resolvingContact = false;
    private loadedCompanyId?: number;
    private sourceExplicitlySelected = false;
    private contactResolutionToken = 0;
    private crmDetailToken = 0;
    private reloadToken = 0;
    private localCreateVersion = 0;
    private localCreatedClients: CrmClient[] = [];
    private readonly partyFilter$ = new Subject<string>();
    private readonly destroyed$ = new Subject<void>();
    quickCreateVisible = false;
    quickCreateMode: CrmQuickCreateMode | null = null;

    constructor(
        private crm: CrmService,
        private crmPartyContext: CrmPartyContextService,
        private parties: PartyService,
        private customers: CustomerService
    ) {}

    ngOnInit(): void {
        this.partyFilter$.pipe(
            debounceTime(250),
            distinctUntilChanged(),
            switchMap(term => {
                const companyId = this.owningCompanyId!;
                return this.parties.getParties(companyId, [], term, 0, 100).pipe(
                    map(page => ({ page, companyId })),
                    catchError(() => of({ page: null, companyId }))
                );
            }),
            takeUntil(this.destroyed$)
        ).subscribe(({ page, companyId }) => {
            if (this.source !== 'PARTY' || companyId !== this.owningCompanyId || !page) return;
            const options = page.content || [];
            const selected = this.selectedLegacyParty;
            this.legacyParties = selected && !options.some(party =>
                party.id.entityId === selected.id.entityId && party.id.entityType === selected.id.entityType)
                ? [selected, ...options]
                : options;
        });
        this.reload();
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['owningCompanyId'] && !changes['owningCompanyId'].firstChange) this.reload();
        if (changes['value'] && !changes['value'].firstChange) this.applyValue();
        if ((changes['partySnapshot'] || changes['partyContactSnapshot'] || changes['crmClientSnapshot'] || changes['crmContactSnapshot']) && this.value) this.applyValue();
        if (changes['roles'] && !changes['roles'].firstChange) this.reload();
        if (changes['allowLegacy'] && !this.allowLegacy && this.source !== 'CRM') this.selectSource('CRM');
    }

    selectSource(source: CommercialPartySource): void {
        if (source !== 'CRM' && !this.allowLegacy) return;
        this.sourceExplicitlySelected = true;
        this.contactResolutionToken++;
        this.resolvingContact = false;
        this.source = source;
        if (source === 'CRM') {
            this.selectedLegacyParty = null;
            this.selectedLegacyContact = null;
        } else {
            this.selectedClient = null;
            this.selectedAccount = null;
            this.selectedContact = null;
            this.accounts = [];
            this.contacts = [];
        }
        this.emitSelection();
    }

    onClientChange(): void {
        this.contactResolutionToken++;
        this.resolvingContact = false;
        this.selectedAccount = null;
        this.selectedContact = null;
        this.accounts = [];
        this.contacts = [];
        this.loadCrmDetails(this.selectedClient?.id, true);
    }

    openQuickCreate(mode: CrmQuickCreateMode): void {
        if (this.disabled || !this.owningCompanyId || (mode !== 'client' && !this.selectedClient?.id)) return;
        this.quickCreateMode = mode;
        this.quickCreateVisible = true;
    }

    onQuickCreated(result: CrmQuickCreateResult): void {
        this.quickCreated.emit(result);
        this.localCreateVersion++;
        this.contactResolutionToken++;
        if (result.mode === 'client') {
            this.source = 'CRM';
            this.selectedClient = result.record;
            this.selectedAccount = null;
            this.selectedContact = null;
            this.accounts = [];
            this.contacts = [];
            this.localCreatedClients = [...this.localCreatedClients.filter(client => client.id !== result.record.id), result.record];
            this.clients = [...this.clients.filter(client => client.id !== result.record.id), result.record]
                .sort((a, b) => a.companyName.localeCompare(b.companyName));
            this.emitSelection();
            this.loadCrmDetails(result.record.id, false);
        } else if (result.mode === 'account') {
            this.selectedAccount = result.record;
            this.selectedContact = null;
            this.accounts = [...this.accounts.filter(account => account.id !== result.record.id), result.record];
            this.emitSelection();
            this.loadCrmDetails(this.selectedClient?.id, true, result.record.id);
        } else {
            this.selectedContact = {
                crmClientContactId: result.record.id,
                customerId: result.record.customerId,
                name: result.record.name,
                email: result.record.email,
                phone: result.record.phone,
                mobile: result.record.mobile,
                clientContact: !result.record.customerId,
                linkedCompanyContact: !!result.record.customerId,
                accounts: this.selectedAccount?.id ? [{ id: this.selectedAccount.id, name: this.selectedAccount.companyName }] : []
            };
            this.contacts = [...this.contacts.filter(contact => contact.crmClientContactId !== result.record.id), this.selectedContact];
            this.emitSelection();
            this.loadCrmDetails(this.selectedClient?.id, true, this.selectedAccount?.id, result.record.id);
        }
    }

    onAccountChange(): void {
        this.contactResolutionToken++;
        this.resolvingContact = false;
        this.selectedContact = null;
        this.contacts = [];
        this.loadCrmDetails(this.selectedClient?.id, true, this.selectedAccount?.id);
    }

    onContactChange(): void {
        const resolutionToken = ++this.contactResolutionToken;
        const contact = this.selectedContact;
        if (!contact || contact.crmClientContactId) {
            this.emitSelection();
            return;
        }
        if (!contact.customerId || !this.selectedClient?.id || !this.owningCompanyId) {
            this.selectedContact = null;
            this.emitSelection();
            return;
        }
        this.resolvingContact = true;
        this.crm.resolveClientContactDirectoryEntry(this.selectedClient.id, {
            customerId: contact.customerId,
            accountId: this.selectedAccount?.id
        }, this.owningCompanyId).subscribe({
            next: response => {
                if (resolutionToken !== this.contactResolutionToken) return;
                if (response.success && response.data?.id && this.selectedContact?.customerId === contact.customerId) {
                    this.selectedContact = { ...this.selectedContact, crmClientContactId: response.data.id };
                }
                this.resolvingContact = false;
                this.emitSelection();
            },
            error: () => {
                if (resolutionToken !== this.contactResolutionToken) return;
                this.resolvingContact = false;
                this.selectedContact = null;
                this.emitSelection();
            }
        });
    }

    onLegacyPartyChange(): void {
        this.selectedLegacyContact = null;
        this.legacyContacts = [];
        this.emitSelection();
        if (this.selectedLegacyParty?.id.entityType === PartyType.Company) {
            this.loadLegacyContacts(this.selectedLegacyParty.id.entityId);
        }
    }

    onPartyFilter(term: string): void {
        if (this.partySource === 'PARTY' && this.source === 'PARTY' && this.owningCompanyId) {
            this.partyFilter$.next(term);
        }
    }

    hasParty(): boolean { return this.source === 'CRM' ? !!this.selectedClient?.id : !!this.selectedLegacyParty?.id?.entityId; }

    hasRequiredSelection(): boolean {
        return this.hasParty() && (!this.requireCrmAccount || !!this.selectedAccount?.id);
    }

    emitSelection(): void {
        if (this.source === 'CRM') {
            this.valueChange.emit(normalizeCommercialPartyReference({
                source: 'CRM',
                crmClientId: this.selectedClient?.id,
                crmAccountId: this.selectedAccount?.id,
                crmContactId: this.selectedContact?.crmClientContactId
            }));
            return;
        }
        if (this.source === 'PARTY') {
            this.valueChange.emit(normalizeCommercialPartyReference({
                source: 'PARTY',
                partyEntityId: this.selectedLegacyParty?.id.entityId,
                partyEntityType: this.selectedLegacyParty?.id.entityType,
                partyContactEntityId: this.selectedLegacyContact?.id.entityId,
                partyContactEntityType: this.selectedLegacyContact?.id.entityType
            }));
            return;
        }
        const isCompany = this.selectedLegacyParty?.id.entityType === PartyType.Company;
        this.valueChange.emit(normalizeCommercialPartyReference({
            source: 'LEGACY',
            legacyCompanyId: isCompany ? this.selectedLegacyParty?.id.entityId : undefined,
            legacyCustomerId: isCompany
                ? this.selectedLegacyContact?.id.entityId
                : this.selectedLegacyParty?.id.entityId
        }));
    }

    private reload(): void {
        if (!this.owningCompanyId) return;
        const token = ++this.reloadToken;
        const createVersion = this.localCreateVersion;
        this.loading = true;
        this.loadedCompanyId = this.owningCompanyId;
        forkJoin({
            clients: this.crmPartyContext.loadClients(this.owningCompanyId, this.roles),
            parties: this.allowLegacy
                ? this.parties.getParties(this.owningCompanyId,
                    this.partySource === 'PARTY' ? [] : [PartyType.Company, PartyType.Customer], '', 0, 1000)
                : of(null)
        }).subscribe({
            next: ({ clients, parties }) => {
                if (token !== this.reloadToken || this.loadedCompanyId !== this.owningCompanyId) return;
                this.clients = [...(clients || []), ...this.localCreatedClients.filter(client =>
                    client.companyId === this.owningCompanyId
                    && client.roles?.some(role => this.roles.includes(role))
                    && !clients?.some(item => item.id === client.id))];
                if (this.crmClientSnapshot?.id && !this.clients.some(client => client.id === this.crmClientSnapshot!.id)) {
                    this.clients = [this.crmClientSnapshot, ...this.clients];
                }
                this.legacyParties = parties?.content || [];
                if (this.partySnapshot?.id && !this.legacyParties.some(party =>
                    party.id.entityId === this.partySnapshot!.id.entityId
                    && party.id.entityType === this.partySnapshot!.id.entityType)) {
                    this.legacyParties = [this.partySnapshot, ...this.legacyParties];
                }
                this.loading = false;
                if (createVersion === this.localCreateVersion) this.applyValue();
            },
            error: () => { if (token !== this.reloadToken) return; this.loading = false; if (createVersion === this.localCreateVersion) this.applyValue(); }
        });
    }

    private applyValue(): void {
        let normalized = normalizeCommercialPartyReference(this.value);
        if (!normalized.crmClientId && !normalized.partyEntityId && !this.sourceExplicitlySelected
            && !this.preserveLegacySelection && !this.preservePartySelection) {
            const preferredCrmClient = this.clients.find(client =>
                (normalized.legacyCustomerId && client.linkedCustomerId === normalized.legacyCustomerId)
                || (normalized.legacyCompanyId && client.linkedCompanyId === normalized.legacyCompanyId));
            if (preferredCrmClient?.id) {
                normalized = normalizeCommercialPartyReference({ source: 'CRM', crmClientId: preferredCrmClient.id });
                queueMicrotask(() => this.valueChange.emit(normalized));
            }
        }
        this.source = normalized.crmClientId || !this.allowLegacy ? 'CRM' : normalized.source;
        if (this.source === 'CRM') {
            this.selectedClient = this.clients.find(item => item.id === normalized.crmClientId)
                || (this.crmClientSnapshot?.id === normalized.crmClientId ? this.crmClientSnapshot : null) || null;
            if (this.selectedClient && !this.clients.some(item => item.id === this.selectedClient!.id)) this.clients = [this.selectedClient, ...this.clients];
            this.selectedLegacyParty = null;
            this.selectedLegacyContact = null;
            this.loadCrmDetails(normalized.crmClientId ?? undefined, false, normalized.crmAccountId, normalized.crmContactId);
            return;
        }
        const mainId = normalized.source === 'PARTY'
            ? normalized.partyEntityId
            : normalized.legacyCompanyId || normalized.legacyCustomerId;
        const mainType = normalized.source === 'PARTY'
            ? normalized.partyEntityType
            : normalized.legacyCompanyId ? PartyType.Company : PartyType.Customer;
        this.selectedLegacyParty = this.legacyParties.find(item => item.id.entityId === mainId && item.id.entityType === mainType)
            || (this.partySnapshot?.id.entityId === mainId && this.partySnapshot?.id.entityType === mainType ? this.partySnapshot : null) || null;
        if (this.selectedLegacyParty && !this.legacyParties.some(item => item.id.entityId === mainId && item.id.entityType === mainType)) this.legacyParties = [this.selectedLegacyParty, ...this.legacyParties];
        if (!this.selectedLegacyParty && normalized.source === 'PARTY' && mainId && mainType && this.owningCompanyId) {
            const companyIdAtRequest = this.owningCompanyId;
            this.parties.getPartyById(companyIdAtRequest, mainType, mainId)
                .pipe(catchError(() => of(null)))
                .subscribe(party => {
                    if (!party) return;
                    if (this.source !== 'PARTY' || this.owningCompanyId !== companyIdAtRequest
                        || this.value?.partyEntityId !== mainId || this.value?.partyEntityType !== mainType) return;
                    this.selectedLegacyParty = party;
                    this.legacyParties = [party, ...this.legacyParties.filter(item =>
                        item.id.entityId !== mainId || item.id.entityType !== mainType)];
                    if (mainType === PartyType.Company) {
                        this.loadLegacyContacts(mainId, normalized.partyContactEntityId);
                    }
                });
        }
        this.selectedClient = null;
        this.selectedAccount = null;
        this.selectedContact = null;
        this.selectedLegacyContact = null;
        this.legacyContacts = [];
        const companyId = normalized.source === 'PARTY'
            ? normalized.partyEntityType === PartyType.Company ? normalized.partyEntityId : null
            : normalized.legacyCompanyId;
        if (companyId) this.loadLegacyContacts(companyId,
            normalized.source === 'PARTY' ? normalized.partyContactEntityId : normalized.legacyCustomerId);
        else if (normalized.source === 'PARTY' && this.partyContactSnapshot?.id.entityId === normalized.partyContactEntityId
                && this.partyContactSnapshot?.id.entityType === normalized.partyContactEntityType) {
            this.selectedLegacyContact = this.partyContactSnapshot || null;
            this.legacyContacts = this.selectedLegacyContact ? [this.selectedLegacyContact] : [];
        }
    }

    private loadCrmDetails(clientId?: number, emit = false, accountId?: number | null, contactId?: number | null): void {
        const token = ++this.crmDetailToken;
        if (!clientId || !this.owningCompanyId) {
            this.accounts = [];
            this.contacts = [];
            if (emit) this.emitSelection();
            return;
        }
        forkJoin({
            accounts: this.crm.getAccounts(this.owningCompanyId, clientId),
            contacts: this.crm.getClientContactDirectory(clientId, this.owningCompanyId, accountId)
        }).subscribe(({ accounts, contacts }) => {
            if (token !== this.crmDetailToken || this.selectedClient?.id !== clientId) return;
            this.accounts = accounts || [];
            this.contacts = contacts || [];
            this.selectedAccount = this.accounts.find(item => item.id === accountId) || null;
            if (!this.selectedAccount && accountId && !emit) {
                this.selectedAccount = { id: accountId, companyName: `Saved account #${accountId}`, crmClientId: clientId, companyId: this.owningCompanyId } as CrmAccount;
                this.accounts = [this.selectedAccount, ...this.accounts];
            }
            this.selectedContact = this.contacts.find(item => item.crmClientContactId === contactId)
                || (emit && !accountId ? this.preferredDirectoryContact(this.contacts) : null);
            if (!this.selectedContact && contactId && !emit) {
                const snapshot = this.crmContactSnapshot?.id === contactId ? this.crmContactSnapshot : null;
                this.selectedContact = { crmClientContactId: contactId, name: snapshot?.name || `Saved contact #${contactId}`,
                    email: snapshot?.email, phone: snapshot?.phone, mobile: snapshot?.mobile, clientContact: true };
                this.contacts = [this.selectedContact, ...this.contacts];
            }
            if (emit) this.emitSelection();
        });
    }

    contactDisplayName(contact?: CrmClientContactDirectoryEntry | null): string {
        return contact?.name || contact?.email || contact?.phone || contact?.mobile || 'Unnamed contact';
    }

    private preferredDirectoryContact(contacts: CrmClientContactDirectoryEntry[]): CrmClientContactDirectoryEntry | null {
        return contacts.find(contact => !!contact.crmClientContactId && !!contact.primaryRecord)
            || contacts.find(contact => !!contact.crmClientContactId)
            || null;
    }

    private loadLegacyContacts(companyId: number, contactId?: number | null): void {
        const owningCompanyId = this.owningCompanyId;
        this.customers.getCustomersByCompany(companyId).subscribe(customers => {
            if (this.owningCompanyId !== owningCompanyId || this.selectedLegacyParty?.id.entityType !== PartyType.Company
                || this.selectedLegacyParty.id.entityId !== companyId) return;
            this.legacyContacts = (customers || []).map(customer =>
                this.parties.convertToParty(customer, PartyType.Customer, owningCompanyId!));
            this.selectedLegacyContact = this.legacyContacts.find(item => item.id.entityId === contactId)
                || (this.partyContactSnapshot?.id.entityId === contactId ? this.partyContactSnapshot : null) || null;
            if (this.selectedLegacyContact && !this.legacyContacts.some(item => item.id.entityId === contactId)) {
                this.legacyContacts = [this.selectedLegacyContact, ...this.legacyContacts];
            }
        });
    }
}
